import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Download, FileText, Users, X, Lock, AlertTriangle } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { PageHeader, Pager, istDT } from "../../components/common";

const HOW = { opened: ["Opened", Eye], downloaded: ["Downloaded", Download] };
const inp = "bg-muted/60 border h-9 px-2 text-sm";

const Filters = ({ f, set, people }) => (
  <div className="flex flex-wrap gap-2 mb-4">
    <input data-testid="doclog-search" defaultValue={f.q || ""} placeholder="UID, client or file name…" onKeyDown={(e) => e.key === "Enter" && set({ q: e.target.value || undefined })} onBlur={(e) => (e.target.value || undefined) !== f.q && set({ q: e.target.value || undefined })} className={`${inp} flex-1 min-w-[200px]`} />
    <select data-testid="doclog-person" value={f.user_id || ""} onChange={(e) => set({ user_id: e.target.value || undefined })} className={inp}>
      <option value="">Everyone</option>{people.map((x) => <option key={x.user_id} value={x.user_id}>{x.name}</option>)}</select>
    <select data-testid="doclog-category" value={f.category || ""} onChange={(e) => set({ category: e.target.value || undefined })} className={inp}>
      <option value="">PO and estimates</option><option value="customer">PO / customer document</option><option value="estimate">Estimate / quotation</option></select>
    <select data-testid="doclog-how" value={f.how || ""} onChange={(e) => set({ how: e.target.value || undefined })} className={inp}>
      <option value="">Opened or downloaded</option><option value="opened">Opened</option><option value="downloaded">Downloaded</option></select>
    <label className="text-xs text-muted-foreground flex items-center gap-1">From<input type="date" data-testid="doclog-from" value={f.date_from || ""} onChange={(e) => set({ date_from: e.target.value || undefined })} className={inp} /></label>
    <label className="text-xs text-muted-foreground flex items-center gap-1">To<input type="date" data-testid="doclog-to" value={f.date_to || ""} onChange={(e) => set({ date_to: e.target.value || undefined })} className={inp} /></label>
  </div>
);

const Summary = ({ s }) => (
  <div className="flex flex-wrap gap-6 text-sm mb-4" data-testid="doclog-summary">
    <span className="flex items-center gap-1.5"><Eye className="h-4 w-4 text-primary" /><b className="num">{s.opens}</b> opens</span>
    <span className="flex items-center gap-1.5"><Users className="h-4 w-4 text-primary" /><b className="num">{s.people}</b> people</span>
    <span className="flex items-center gap-1.5"><FileText className="h-4 w-4 text-primary" /><b className="num">{s.documents}</b> documents</span>
  </div>
);

const dayLabel = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short" });

const Flags = ({ d, show }) => (
  <div className="border border-amber-500/50 bg-amber-50 p-4 mb-4" data-testid="doclog-flags">
    <div className="flex items-center gap-2 text-sm font-medium text-amber-900"><AlertTriangle className="h-4 w-4" />Unusual opens in the last {d.flag_days} days</div>
    <p className="text-xs text-amber-900/80 mt-1 mb-2">People who opened {d.flag_files} or more different PO or estimate files in one day. Founders and the Managing Partner are not flagged.</p>
    <div className="flex flex-col divide-y divide-amber-500/20">
      {d.flags.map((x) => (
        <div key={`${x.user_id}-${x.day}`} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5 text-sm" data-testid={`doclog-flag-${x.user_id}-${x.day}`}>
          <b>{x.name}</b><span className="text-amber-900/80">{dayLabel(x.day)}</span>
          <span className="num">{x.files} files · {x.opens} opens</span>
          <button onClick={() => show(x)} className="text-primary hover:underline text-xs" data-testid={`doclog-flag-show-${x.user_id}-${x.day}`}>Show these opens</button>
        </div>))}
    </div>
  </div>
);

const Row = ({ v }) => {
  const [label, Icon] = HOW[v.how] || ["Opened or downloaded", Eye];
  return (
    <tr data-testid={`doclog-row-${v.view_id}`} data-flagged={v.flagged ? "yes" : "no"} className={v.flagged ? "bg-amber-50" : ""}>
      <td className="num text-xs whitespace-nowrap">{istDT(v.at)}</td>
      <td className="text-sm">{v.flagged ? <span className="inline-flex items-center gap-1 text-amber-900" title="Unusual opens that day" data-testid={`doclog-row-flag-${v.view_id}`}><AlertTriangle className="h-3.5 w-3.5" />{v.user_name}</span> : v.user_name}</td>
      <td className="text-xs whitespace-nowrap"><span className="inline-flex items-center gap-1"><Icon className="h-3.5 w-3.5 text-primary" />{label}</span></td>
      <td className="text-sm max-w-[260px] truncate" title={v.file_name}>{v.file_name}</td>
      <td className="text-xs whitespace-nowrap">{v.category_label}</td>
      <td className="font-mono text-xs"><Link to={`/orders/uid/${encodeURIComponent(v.uid)}`} className="text-primary hover:underline" data-testid={`doclog-uid-${v.view_id}`}>{v.uid}</Link></td>
      <td className="text-sm max-w-[220px] truncate">{v.client_name || "—"}</td>
    </tr>
  );
};

const UidChip = ({ uid, clear }) => (
  <div className="inline-flex items-center gap-2 border border-primary/40 text-sm px-2 h-8 mb-3" data-testid="doclog-uid-filter">
    UID <b className="font-mono">{uid}</b>
    <button onClick={clear} title="Show all UIDs" className="text-muted-foreground hover:text-primary" data-testid="doclog-uid-filter-clear"><X className="h-3.5 w-3.5" /></button>
  </div>
);

const Denied = () => (
  <div className="ax-panel p-8 text-sm text-muted-foreground flex items-center gap-2" data-testid="doclog-denied">
    <Lock className="h-4 w-4" />Only founders and the Managing Partner can see the document log.
  </div>
);

export default function DocLog() {
  const [params, setParams] = useSearchParams();
  const f = Object.fromEntries(params.entries());
  const [d, setD] = useState(null);
  const [denied, setDenied] = useState(false);
  const key = params.toString();
  useEffect(() => {
    api.get("/doc-views", { params: Object.fromEntries(new URLSearchParams(key)) }).then((r) => setD(r.data))
      .catch((e) => (e.response?.status === 403 ? setDenied(true) : toast.error(errMsg(e), { id: "doclog-error" })));
  }, [key]);
  const set = (ch) => setParams(Object.fromEntries(Object.entries({ ...f, ...ch, page: ch.page }).filter(([, v]) => v)));
  if (denied) return <Denied />;
  return (
    <div data-testid="doclog-page">
      <PageHeader eyebrow="Order Management" title="Document view log" desc="Who opened or downloaded each PO and estimate document, and when. Only founders and the Managing Partner (Mumbai UIDs) can see this." />
      {f.uid && <UidChip uid={f.uid} clear={() => set({ uid: undefined })} />}
      {d?.flags?.length > 0 && <Flags d={d} show={(x) => setParams({ user_id: x.user_id, date_from: x.day, date_to: x.day })} />}
      <Filters key={f.q || ""} f={f} set={set} people={d?.people || []} />
      {d && <Summary s={d.summary} />}
      <div className="ax-panel ax-scroll"><table className="ax-table w-full min-w-[900px]" data-testid="doclog-table">
        <thead><tr>{["When (IST)", "Person", "Action", "Document", "Type", "UID", "Client"].map((h) => <th key={h}>{h}</th>)}</tr></thead>
        <tbody>{d?.items.map((v) => <Row key={v.view_id} v={v} />)}</tbody>
      </table>
        {d && !d.items.length && <div className="text-sm text-muted-foreground p-4" data-testid="doclog-empty">No one has opened a PO or estimate document{key ? " that matches these filters" : " yet"}.</div>}
      </div>
      {d && <Pager page={d.page} total={d.total} size={d.size} onPage={(pg) => set({ page: String(pg) })} />}
    </div>
  );
}
